import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

function Cotizar() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const registroExitoso = searchParams.get('registro') === 'exitoso';
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [formData, setFormData] = useState({
    origen_cp: '', destino_cp: '', tipo_paquete: '', peso: '', largo: '', ancho: '', alto: ''
  });
  const [cotizaciones, setCotizaciones] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const limitesPaquete = {
    envelope: {
      nombre: 'Sobre', largo: 35, ancho: 24, alto: 1.5, peso: 1, editable: false,
      mensaje: 'Las medidas maximas para Sobre son: Largo: 35 cm, Ancho: 24 cm, Alto: 1.5 cm, Peso: 1 kg. Las medidas no se pueden editar.'
    },
    box: {
      nombre: 'Caja', peso_min: 1, peso_max: 69, editable: true,
      mensaje: 'El peso volumetrico o real no puede pasar de 69 kilos. Para paquetes mas grandes intente seleccionando Tarima.'
    },
    pallet: {
      nombre: 'Tarima', largo_min: 1, largo_max: 300, ancho_min: 1, ancho_max: 120,
      alto_min: 1, alto_max: 180, peso_min: 70, peso_max: 2000, editable: true,
      mensaje: 'Las medidas para Tarima son: Largo: entre 1 cm - 300 cm, Ancho: entre 1 cm - 120 cm, Alto: entre 1 cm - 180 cm, Peso: entre 70 kg - 2000 kg.'
    }
  };

  useEffect(function() {
    if (formData.tipo_paquete === 'envelope') {
      setFormData(function(prev) { return { ...prev, largo: '35', ancho: '24', alto: '1.5', peso: '1' }; });
    } else if (formData.tipo_paquete && formData.tipo_paquete !== 'envelope') {
      setFormData(function(prev) { return { ...prev, largo: '', ancho: '', alto: '', peso: '' }; });
    }
  }, [formData.tipo_paquete]);

  var handleChange = function(e) {
    var name = e.target.name;
    var value = e.target.value;
    if (formData.tipo_paquete === 'envelope' && ['largo', 'ancho', 'alto', 'peso'].includes(name)) return;
    setFormData({ ...formData, [name]: value });
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    var limites = limitesPaquete[formData.tipo_paquete];

    if (formData.tipo_paquete === 'box') {
      var peso = parseFloat(formData.peso);
      var largo = parseFloat(formData.largo);
      var ancho = parseFloat(formData.ancho);
      var alto = parseFloat(formData.alto);
      if (peso < 1 || peso > 69) { setError('El peso real debe estar entre 1 kg y 69 kg para Caja'); return; }
      var pesoVolumetrico = (largo * ancho * alto) / 5000;
      if (pesoVolumetrico > 69) { setError('El peso volumetrico (' + pesoVolumetrico.toFixed(2) + ' kg) excede el maximo de 69 kg.'); return; }
    }

    if (formData.tipo_paquete === 'pallet') {
      peso = parseFloat(formData.peso);
      largo = parseFloat(formData.largo);
      ancho = parseFloat(formData.ancho);
      alto = parseFloat(formData.alto);
      if (peso < limites.peso_min || peso > limites.peso_max) { setError('El peso debe estar entre ' + limites.peso_min + ' kg y ' + limites.peso_max + ' kg'); return; }
      if (largo < limites.largo_min || largo > limites.largo_max) { setError('El largo debe estar entre ' + limites.largo_min + ' cm y ' + limites.largo_max + ' cm'); return; }
      if (ancho < limites.ancho_min || ancho > limites.ancho_max) { setError('El ancho debe estar entre ' + limites.ancho_min + ' cm y ' + limites.ancho_max + ' cm'); return; }
      if (alto < limites.alto_min || alto > limites.alto_max) { setError('El alto debe estar entre ' + limites.alto_min + ' cm y ' + limites.alto_max + ' cm'); return; }
    }

    setLoading(true);
    setShowAllOptions(false);
    try {
      var response = await api.post('/cotizacion', formData);
      setCotizaciones(response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'Error al cotizar');
    } finally {
      setLoading(false);
    }
  };

  var handleSeleccionarServicio = function(servicio) {
    sessionStorage.setItem('cotizacion_previa', JSON.stringify({
      origen_cp: formData.origen_cp, destino_cp: formData.destino_cp, tipo_paquete: formData.tipo_paquete,
      peso: formData.peso, largo: formData.largo, ancho: formData.ancho, alto: formData.alto,
      servicio_seleccionado: servicio,
      cotizaciones: cotizaciones
    }));
    navigate('/crear-envio');
  };

  var handleLogout = function() { logout(); navigate('/login'); };

  var inputStyle = {
    width: '100%', padding: '0.625rem 0.875rem', border: '1.5px solid #e2e5ea',
    borderRadius: '0.5rem', fontSize: '0.95rem', boxSizing: 'border-box',
    backgroundColor: '#fff', transition: 'border-color 0.2s ease',
    outline: 'none', color: '#1a1a2e'
  };

  var disabledInputStyle = {
    ...inputStyle, backgroundColor: '#f8f9fb', cursor: 'not-allowed', color: '#9ca3af'
  };

  // ============ FILTER & CATEGORIZE SERVICES ============
  var filteredServices = [];
  var topPicks = [];
  var remainingServices = [];

  if (cotizaciones && cotizaciones.servicios && cotizaciones.servicios.length > 0) {
    filteredServices = cotizaciones.servicios.filter(function(s) {
      var carrier = (s.paqueteria || '').toLowerCase();
      var source = (s.fuente || '').toLowerCase();
      if (carrier.includes('dhl') && source === 'pak2go') return false;
      return true;
    });

    // Step 3: Remove duplicates - same carrier + similar service, keep cheapest
    var seen = {};
    filteredServices = filteredServices.filter(function(s) {
      var key = (s.paqueteria || '').toLowerCase() + '_' + (s.servicio || '').toLowerCase().replace(/zona?\s*\d+/gi, '').trim();
      if (seen[key]) {
        if (s.costo < seen[key].costo) {
          // Replace with cheaper one
          var idx = filteredServices.indexOf(seen[key]);
          seen[key] = s;
          return true;
        }
        return false;
      }
      seen[key] = s;
      return true;
    });

    // Step 4: Sort by price
    filteredServices.sort(function(a, b) { return a.costo - b.costo; });

    // Step 5: Identify Top 3 Picks
    // Recommended: Paquetexpress (best value)
    var recommended = filteredServices.find(function(s) {
      return (s.paqueteria || '').toLowerCase().includes('paquetexpress');
    });

    // Cheapest: lowest cost
    var cheapest = filteredServices[0] || null;

    // Fastest: DHL Express Domestic from Epack
    var fastest = filteredServices.find(function(s) {
      var carrier = (s.paqueteria || '').toLowerCase();
      var service = (s.servicio || '').toLowerCase();
      return carrier.includes('dhl') && (service.includes('express domestic') || service.includes('express'));
    });

    // If no DHL express, find lowest days
    if (!fastest) {
      var sorted = [...filteredServices].sort(function(a, b) {
        var daysA = parseInt(a.tiempo_entrega) || 99;
        var daysB = parseInt(b.tiempo_entrega) || 99;
        return daysA - daysB;
      });
      fastest = sorted[0];
    }

    // Build top picks (avoid duplicates in top 3)
    var topIds = new Set();

    if (recommended) {
      topPicks.push({ ...recommended, _role: 'recommended' });
      topIds.add(recommended.id);
    }

    if (cheapest && !topIds.has(cheapest.id)) {
      topPicks.push({ ...cheapest, _role: 'cheapest' });
      topIds.add(cheapest.id);
    } else if (cheapest && topIds.has(cheapest.id)) {
      // Cheapest is same as recommended, find next cheapest
      var nextCheapest = filteredServices.find(function(s) { return !topIds.has(s.id); });
      if (nextCheapest) {
        topPicks.push({ ...nextCheapest, _role: 'cheapest' });
        topIds.add(nextCheapest.id);
      }
    }

    if (fastest && !topIds.has(fastest.id)) {
      topPicks.push({ ...fastest, _role: 'fastest' });
      topIds.add(fastest.id);
    } else if (fastest && topIds.has(fastest.id)) {
      // Find next fastest
      var nextFastest = filteredServices.find(function(s) {
        if (topIds.has(s.id)) return false;
        var d = parseInt(s.tiempo_entrega) || 99;
        return d <= 3;
      });
      if (nextFastest) {
        topPicks.push({ ...nextFastest, _role: 'fastest' });
        topIds.add(nextFastest.id);
      }
    }

    // Remaining services (not in top picks)
    remainingServices = filteredServices.filter(function(s) {
      return !topIds.has(s.id);
    });
  }

  // ============ HELPER: format carrier name ============
  var formatCarrier = function(name) {
    if (!name) return '';
    var n = name.toLowerCase();
    if (n.includes('paquetexpress')) return 'Paquetexpress';
    if (n.includes('dhl')) return 'DHL';
    if (n.includes('estafeta')) return 'Estafeta';
    if (n.includes('fedex')) return 'FedEx';
    if (n.includes('redpack')) return 'Redpack';
    return name;
  };

  // ============ HELPER: format service name (remove technical codes) ============
  var formatService = function(servicio) {
    if (!servicio) return '';
    // Remove zone codes like "Zona 3", "Z3", etc
    var clean = servicio.replace(/\s*(Zona|Z)\s*\d+/gi, '').trim();
    // Clean up known technical names
    clean = clean.replace(/STANDARD_SPECIAL/gi, 'Terrestre Especial');
    clean = clean.replace(/STANDARD/gi, 'Terrestre');
    clean = clean.replace(/EXPRESS_SPECIAL/gi, 'Express Especial');
    clean = clean.replace(/EXPRESS_Z\d+/gi, 'Express');
    clean = clean.replace(/EXPRESS DOMESTIC/gi, 'Express Domestico');
    clean = clean.replace(/Nacional/gi, 'Terrestre Nacional');
    clean = clean.replace(/_/g, ' ');
    // Capitalize first letter of each word
    clean = clean.replace(/\b\w/g, function(l) { return l.toUpperCase(); });
    return clean || servicio;
  };

  // ============ HELPER: extract delivery days number ============
  var extractDays = function(tiempo) {
    if (!tiempo) return '';
    var match = tiempo.match(/(\d+)/);
    return match ? match[1] : '';
  };

  // ============ ROLE CONFIG ============
  var roleConfig = {
    recommended: {
      label: 'Recomendada',
      sublabel: 'Mejor calidad-precio',
      badgeBg: '#ff8c00',
      badgeColor: '#fff',
      borderColor: '#ff8c00',
      shadowColor: 'rgba(255,140,0,0.15)',
      accentColor: '#ff8c00',
      icon: '\u2B50',
      ctaText: 'Continuar con esta opcion',
      ctaFilled: true
    },
    cheapest: {
      label: 'Mas economica',
      sublabel: 'Precio mas bajo',
      badgeBg: '#059669',
      badgeColor: '#fff',
      borderColor: '#d1d5db',
      shadowColor: 'rgba(0,0,0,0.06)',
      accentColor: '#059669',
      icon: '\uD83D\uDCB0',
      ctaText: 'Seleccionar',
      ctaFilled: false
    },
    fastest: {
      label: 'Mas rapida',
      sublabel: 'Entrega express',
      badgeBg: '#2563eb',
      badgeColor: '#fff',
      borderColor: '#d1d5db',
      shadowColor: 'rgba(0,0,0,0.06)',
      accentColor: '#2563eb',
      icon: '\uD83D\uDE80',
      ctaText: 'Seleccionar',
      ctaFilled: false
    }
  };

  // ============ RENDER SERVICE CARD ============
  var renderServiceCard = function(servicio, config, isTopPick) {
    var carrier = formatCarrier(servicio.paqueteria);
    var serviceName = formatService(servicio.servicio);
    var days = extractDays(servicio.tiempo_entrega);
    var isRec = config && config.ctaFilled;

    return (
      <div
        key={servicio.id + (config ? config.label : '')}
        style={{
          backgroundColor: '#fff',
          borderRadius: '0.875rem',
          padding: isMobile ? '1.125rem' : '1.5rem',
          border: isTopPick && isRec ? '2px solid ' + config.borderColor : '1.5px solid #e5e7eb',
          boxShadow: isTopPick && isRec ? '0 8px 30px ' + config.shadowColor : '0 2px 8px rgba(0,0,0,0.04)',
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={function(e) {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={function(e) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = isTopPick && isRec ? '0 8px 30px ' + config.shadowColor : '0 2px 8px rgba(0,0,0,0.04)';
        }}
        onClick={function() { handleSeleccionarServicio(servicio); }}
      >
        {/* Badge for top picks */}
        {config && isTopPick && (
          <div style={{
            position: 'absolute',
            top: '-0.75rem',
            left: isMobile ? '0.875rem' : '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            backgroundColor: config.badgeBg,
            color: config.badgeColor,
            padding: '0.25rem 0.875rem',
            borderRadius: '9999px',
            fontSize: '0.72rem',
            fontWeight: '700',
            letterSpacing: '0.03em',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
          }}>
            <span>{config.icon}</span>
            <span>{config.label}</span>
          </div>
        )}

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '1rem' : '1.25rem',
          marginTop: config && isTopPick ? '0.375rem' : '0'
        }}>
          {/* Left: carrier + service info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Carrier name - BIG */}
            <div style={{
              fontSize: isMobile ? '1.125rem' : '1.25rem',
              fontWeight: '800',
              color: '#0f172a',
              lineHeight: 1.2,
              letterSpacing: '-0.01em'
            }}>
              {carrier}
            </div>
            {/* Service name - smaller */}
            <div style={{
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              color: '#64748b',
              fontWeight: '500',
              marginTop: '0.2rem',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {serviceName}
              {servicio.cancelable === false && (
                <span style={{
                  display: 'inline-block',
                  backgroundColor: '#fff3e0',
                  color: '#e65100',
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  marginLeft: '8px',
                  fontWeight: '600'
                }}>
                  ⚠️ No cancelable · Sin recolección
                </span>
              )}
            </div>
            {/* Pills row */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.625rem'
            }}>
              {days && (
                <span style={{
                  fontSize: '0.72rem', fontWeight: '600',
                  color: parseInt(days) <= 2 ? '#059669' : '#64748b',
                  backgroundColor: parseInt(days) <= 2 ? '#ecfdf5' : '#f1f5f9',
                  padding: '0.2rem 0.625rem', borderRadius: '9999px',
                  letterSpacing: '0.01em'
                }}>
                  {days === '1' ? '1 dia' : days + ' dias'}
                </span>
              )}
              {config && config.sublabel && isTopPick && (
                <span style={{
                  fontSize: '0.72rem', fontWeight: '600',
                  color: config.accentColor,
                  backgroundColor: config.accentColor + '10',
                  padding: '0.2rem 0.625rem', borderRadius: '9999px'
                }}>
                  {config.sublabel}
                </span>
              )}
            </div>
          </div>

          {/* Right: price + CTA */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'row' : 'column',
            alignItems: isMobile ? 'center' : 'flex-end',
            justifyContent: isMobile ? 'space-between' : 'center',
            gap: '0.625rem',
            flexShrink: 0
          }}>
            <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <span style={{
                fontSize: isMobile ? '1.375rem' : '1.625rem',
                fontWeight: '800',
                color: isRec ? '#ff8c00' : '#0f172a',
                letterSpacing: '-0.02em'
              }}>
                ${servicio.costo.toFixed(2)}
              </span>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: '0.25rem', fontWeight: '500' }}>MXN</span>
            </div>
            <div style={{
              padding: isMobile ? '0.625rem 1.5rem' : '0.7rem 1.75rem',
              background: config && config.ctaFilled ? 'linear-gradient(135deg, #ff8c00, #e67e00)' : '#fff',
              color: config && config.ctaFilled ? '#fff' : '#ff8c00',
              border: config && config.ctaFilled ? 'none' : '1.5px solid #ff8c00',
              borderRadius: '0.5rem',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              fontWeight: '700',
              textAlign: 'center',
              boxShadow: config && config.ctaFilled ? '0 4px 14px rgba(255,140,0,0.35)' : 'none',
              whiteSpace: 'nowrap',
              letterSpacing: '0.01em'
            }}>
              {config ? config.ctaText : 'Seleccionar'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fb' }}>
      {/* Header */}
      <nav style={{ backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderBottom: '1px solid #eef0f4' }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: isMobile ? '0 0.75rem' : '0 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', height: isMobile ? '3.5rem' : '4.5rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={function() { navigate('/dashboard'); }}>
              <img src="/logo.png" alt="Logo" style={{ height: isMobile ? '4.5rem' : '7rem' }} />
            </div>
            {isMobile ? (
              <div style={{ position: 'relative' }}>
                <button onClick={function() { setMenuOpen(!menuOpen); }} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0.5rem' }}>&#9776;</button>
                {menuOpen && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', backgroundColor: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: '0.75rem', padding: '0.5rem', minWidth: '180px', zIndex: 1000, border: '1px solid #eef0f4' }}>
                    <button onClick={function() { navigate('/dashboard'); setMenuOpen(false); }} style={{ width: '100%', padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#374151', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: '0.375rem' }}>Dashboard</button>
                    <button onClick={handleLogout} style={{ width: '100%', padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#374151', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: '0.375rem' }}>Cerrar Sesion</button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button onClick={function() { navigate('/dashboard'); }} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#475569', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '500' }}>Dashboard</button>
                <button onClick={handleLogout} style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#475569', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '500' }}>Cerrar Sesion</button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main */}
      <div style={{ maxWidth: '52rem', margin: '0 auto', padding: isMobile ? '1.25rem 0.875rem' : '2.5rem 1.5rem' }}>
        {registroExitoso && (
          <div style={{
            backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', color: '#065f46',
            padding: '1rem 1.25rem', borderRadius: '0.75rem', marginBottom: '1.25rem',
            textAlign: 'center', fontWeight: '600', fontSize: isMobile ? '0.9rem' : '0.95rem'
          }}>
            &#127881; Cuenta creada con exito! Cotiza tu primer envio.
          </div>
        )}

        <h1 style={{
          fontSize: isMobile ? '1.375rem' : '1.75rem', fontWeight: '800',
          color: '#0f172a', marginBottom: isMobile ? '1rem' : '1.5rem',
          letterSpacing: '-0.02em'
        }}>
          Cotizar Envio
        </h1>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.85rem'
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ width: '100%', maxWidth: '400px', height: '8px', backgroundColor: '#e2e5ea', borderRadius: '9999px', margin: '0 auto', overflow: 'hidden' }}>
                <div style={{ width: '60%', height: '100%', background: 'linear-gradient(135deg, #ff8c00, #e67e00)', borderRadius: '9999px', animation: 'loading 1.5s ease-in-out infinite' }} />
              </div>
            </div>
            <p style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.5rem' }}>Buscando las mejores opciones...</p>
            <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Comparando precios en DHL, FedEx, Estafeta, Paquetexpress y mas</p>
            <style>{`@keyframes loading { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }`}</style>
          </div>
        ) : !cotizaciones ? (
          <div style={{
            backgroundColor: '#fff', borderRadius: '1rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #eef0f4',
            padding: isMobile ? '1.25rem' : '2rem'
          }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem', letterSpacing: '0.02em' }}>CP Origen *</label>
                  <input type="text" name="origen_cp" required maxLength="5" value={formData.origen_cp} onChange={handleChange} style={inputStyle} placeholder="31000" onFocus={function(e) { e.target.style.borderColor = '#ff8c00'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e5ea'; }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem', letterSpacing: '0.02em' }}>CP Destino *</label>
                  <input type="text" name="destino_cp" required maxLength="5" value={formData.destino_cp} onChange={handleChange} style={inputStyle} placeholder="06000" onFocus={function(e) { e.target.style.borderColor = '#ff8c00'; }} onBlur={function(e) { e.target.style.borderColor = '#e2e5ea'; }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem', letterSpacing: '0.02em' }}>Tipo de Paquete *</label>
                  <select name="tipo_paquete" required value={formData.tipo_paquete} onChange={handleChange} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">Selecciona tipo de paquete...</option>
                    <option value="envelope">Sobre</option>
                    <option value="box">Caja</option>
                    <option value="pallet">Tarima</option>
                  </select>
                </div>

                {formData.tipo_paquete && (
                  <div style={{
                    gridColumn: '1 / -1', padding: isMobile ? '0.75rem' : '1rem',
                    backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem',
                    color: '#1e40af', fontSize: isMobile ? '0.78rem' : '0.82rem', lineHeight: 1.5
                  }}>
                    {limitesPaquete[formData.tipo_paquete].mensaje}
                  </div>
                )}

                {formData.tipo_paquete && (
                  <>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem' }}>Peso (kg) *</label>
                      <input type="number" name="peso" required step="0.1" value={formData.peso} onChange={handleChange}
                        disabled={formData.tipo_paquete === 'envelope'} style={formData.tipo_paquete === 'envelope' ? disabledInputStyle : inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem' }}>Largo (cm) *</label>
                      <input type="number" name="largo" required value={formData.largo} onChange={handleChange}
                        disabled={formData.tipo_paquete === 'envelope'} style={formData.tipo_paquete === 'envelope' ? disabledInputStyle : inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem' }}>Ancho (cm) *</label>
                      <input type="number" name="ancho" required value={formData.ancho} onChange={handleChange}
                        disabled={formData.tipo_paquete === 'envelope'} style={formData.tipo_paquete === 'envelope' ? disabledInputStyle : inputStyle} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '0.375rem' }}>Alto (cm) *</label>
                      <input type="number" name="alto" required value={formData.alto} onChange={handleChange}
                        disabled={formData.tipo_paquete === 'envelope'} style={formData.tipo_paquete === 'envelope' ? disabledInputStyle : inputStyle} />
                    </div>
                  </>
                )}
              </div>

              <button type="submit" disabled={loading || !formData.tipo_paquete}
                style={{
                  width: '100%', padding: '0.875rem',
                  background: (loading || !formData.tipo_paquete) ? '#fdd8a5' : 'linear-gradient(135deg, #ff8c00, #e67e00)',
                  color: '#fff', fontWeight: '700', borderRadius: '0.625rem', border: 'none',
                  cursor: (loading || !formData.tipo_paquete) ? 'not-allowed' : 'pointer',
                  fontSize: '1rem', letterSpacing: '0.01em',
                  boxShadow: (loading || !formData.tipo_paquete) ? 'none' : '0 4px 14px rgba(255,140,0,0.3)',
                  transition: 'all 0.2s ease'
                }}>
                {loading ? 'Cotizando...' : 'Cotizar envio'}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <button onClick={function() { setCotizaciones(null); setShowAllOptions(false); }} style={{
              marginBottom: '1.25rem', padding: '0.5rem 1rem', backgroundColor: '#fff',
              border: '1.5px solid #e2e5ea', borderRadius: '0.5rem', cursor: 'pointer',
              fontSize: '0.825rem', fontWeight: '600', color: '#475569',
              transition: 'all 0.15s ease'
            }}>
              &larr; Nueva cotizacion
            </button>

            {/* Curated header message */}
            <div style={{
              backgroundColor: '#fff', border: '1px solid #eef0f4', borderRadius: '0.875rem',
              padding: isMobile ? '1rem 1.125rem' : '1.25rem 1.5rem', marginBottom: '1.5rem',
              boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
            }}>
              <p style={{
                fontSize: isMobile ? '0.88rem' : '0.95rem', color: '#334155',
                margin: 0, lineHeight: 1.65, fontWeight: '400'
              }}>
                <span style={{ fontWeight: '700', color: '#0f172a' }}>Seleccionamos las mejores opciones para tu envio</span> segun precio, velocidad y cobertura. {remainingServices.length > 0 ? 'Puedes ver todas las opciones si lo deseas.' : ''}
              </p>
            </div>

            {/* Top 3 Picks */}
            <div style={{ display: 'grid', gap: '1.125rem', marginBottom: '1.5rem' }}>
              {topPicks.map(function(servicio) {
                var config = roleConfig[servicio._role];
                return renderServiceCard(servicio, config, true);
              })}
            </div>

            {/* Show more button */}
            {remainingServices.length > 0 && !showAllOptions && (
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <button
                  onClick={function() { setShowAllOptions(true); }}
                  style={{
                    padding: '0.7rem 2rem',
                    backgroundColor: '#fff',
                    border: '1.5px solid #cbd5e1',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#475569',
                    transition: 'all 0.15s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                  }}
                  onMouseEnter={function(e) { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#fff'; }}
                >
                  Ver {remainingServices.length} opciones mas
                </button>
              </div>
            )}

            {/* Remaining services */}
            {showAllOptions && remainingServices.length > 0 && (
              <div>
                <div style={{
                  fontSize: '0.78rem', fontWeight: '600', color: '#94a3b8',
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  marginBottom: '0.75rem', paddingLeft: '0.25rem'
                }}>
                  Otras opciones disponibles
                </div>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {remainingServices.map(function(servicio) {
                    return renderServiceCard(servicio, null, false);
                  })}
                </div>
              </div>
            )}

            {/* Trust block */}
            <div style={{
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? '1rem' : '2rem',
              flexWrap: 'wrap',
              padding: '1.125rem',
              backgroundColor: '#fff',
              borderRadius: '0.75rem',
              border: '1px solid #eef0f4',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
            }}>
              {[
                { icon: '\uD83D\uDD12', text: 'Pago seguro' },
                { icon: '\u2705', text: 'Guias oficiales' },
                { icon: '\uD83D\uDCF1', text: 'Soporte WhatsApp' }
              ].map(function(item, i) {
                return (
                  <span key={i} style={{
                    fontSize: isMobile ? '0.73rem' : '0.8rem',
                    color: '#64748b',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    letterSpacing: '0.01em'
                  }}>
                    {item.icon} {item.text}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Cotizar;
